import { CustomData, IframeDialogClosed, OpenIframeDialog } from "@components/utils/IframeDialog/IframeDialog";
import { DepositDrawerSubScreen } from "@components/utils/DepositDrawer/types";
import { closeDeposit } from "@components/utils/DepositDrawer/utils";
import { closeIframeDialog } from "@components/utils/IframeDialog";
import toast from "@components/utils/Toaster/toast";
import { useStatsigTracking } from "@configs/statsig";
import { DepositIframeCloseReason, DepositNativeCloseReason } from "@configs/statsig/types";
import { WSDepositFailedEvent } from "@constants/ws";
import { AppDispatch } from "@stores";
import preferenceSlice from "@stores/preference/slices";
import { isGameIframeDialogOpen } from "@utils/ui";
import { useCallback, useEffect, useRef } from "react";
import reactHotToast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useWSDepositFailed } from "./useAppWS";
import useContactLinks from "./useContactLinks";
import usePreference from "./usePreference";

interface PendingNotification {
  baseMessage: string;
  reasonLabel?: string;
  reasonValue?: string;
  actionButtonText: string;
  onActionClick: () => void;
  toastId: string;
  orderRef?: string;
}

const MAX_PENDING_NOTIFICATIONS = 10;
const MAX_ACTIVE_TOAST_IDS = 50;

export default function useDepositFailedNotification() {
  const { t } = useTranslation();
  const { openContact } = useContactLinks();
  const { pendingDepositOrderRef } = usePreference();
  const dispatch = useDispatch<AppDispatch>();
  const { trackCloseDepositCustom } = useStatsigTracking();
  const pendingNotificationsRef = useRef<PendingNotification[]>([]);
  const activeToastIdsRef = useRef<Set<string>>(new Set());

  const showNotification = useCallback((notification: PendingNotification) => {
    toast.warningWithAction(notification.baseMessage, notification.actionButtonText, notification.onActionClick, {
      id: notification.toastId,
      reasonLabel: notification.reasonLabel,
      reasonValue: notification.reasonValue,
    });
    activeToastIdsRef.current.add(notification.toastId);

    // Limit the size of activeToastIdsRef to prevent memory leaks
    if (activeToastIdsRef.current.size > MAX_ACTIVE_TOAST_IDS) {
      const idsArray = Array.from(activeToastIdsRef.current);
      // Remove the oldest half of the IDs
      const idsToRemove = idsArray.slice(0, Math.floor(idsArray.length / 2));
      idsToRemove.forEach((id) => {
        activeToastIdsRef.current.delete(id);
        reactHotToast.dismiss(id);
      });
    }
  }, []);

  useEffect(() => {
    const activeToastIdsRefValue = activeToastIdsRef.current;
    const pendingNotificationsRefValue = pendingNotificationsRef.current;

    const handleIframeOpen = (e: Event) => {
      const customEvent = e as CustomEvent<CustomData>;
      const isGameIframe = customEvent.detail.options?.isGame === true;

      if (isGameIframe) {
        // Dismiss all currently shown deposit failed toasts
        activeToastIdsRefValue.forEach((toastId) => {
          reactHotToast.dismiss(toastId);
        });
        activeToastIdsRefValue.clear();
        // Clear any pending notifications
        pendingNotificationsRef.current.length = 0;
      }
    };

    const handleIframeClose = () => {
      // Show all pending notifications
      pendingNotificationsRefValue.forEach((notification) => {
        showNotification(notification);
      });
      pendingNotificationsRef.current.length = 0;
    };

    window.addEventListener(OpenIframeDialog, handleIframeOpen);
    window.addEventListener(IframeDialogClosed, handleIframeClose);

    // Cleanup on unmount
    return () => {
      window.removeEventListener(OpenIframeDialog, handleIframeOpen);
      window.removeEventListener(IframeDialogClosed, handleIframeClose);
      // Dismiss all active toasts
      activeToastIdsRefValue.forEach((toastId) => {
        reactHotToast.dismiss(toastId);
      });
      // Clear refs
      activeToastIdsRefValue.clear();
      pendingNotificationsRef.current.length = 0;
    };
  }, [showNotification]);

  const handleDepositFailed = useCallback(
    (event: WSDepositFailedEvent) => {
      const fundingTx = event.detail.fundingTx;

      if (pendingDepositOrderRef !== event.detail.fundingTx?.orderRef) {
        return;
      }

      const reasonValue = fundingTx?.memo || "";

      const baseMessage = t("your deposit has failed");
      const reasonLabel = reasonValue ? t("reason") : undefined;
      const actionButtonText = t("contact cs");

      const handleContactCsClick = () => {
        openContact();
      };

      const toastId = `deposit-failed-toast-${fundingTx?.orderRef || Date.now()}`;

      const notification: PendingNotification = {
        baseMessage,
        reasonLabel,
        reasonValue,
        actionButtonText,
        onActionClick: handleContactCsClick,
        toastId,
        orderRef: fundingTx?.orderRef,
      };

      if (isGameIframeDialogOpen()) {
        pendingNotificationsRef.current.push(notification);
        if (pendingNotificationsRef.current.length > MAX_PENDING_NOTIFICATIONS) {
          pendingNotificationsRef.current = pendingNotificationsRef.current.slice(-MAX_PENDING_NOTIFICATIONS);
        }
      } else {
        showNotification(notification);
      }

      closeIframeDialog({
        id: `deposit-${fundingTx?.orderRef}`,
        reason: DepositIframeCloseReason.DEPOSIT_FAILED,
      });
      closeDeposit({
        reason: DepositIframeCloseReason.DEPOSIT_FAILED,
        onTrackClose: (currentSubScreen?: DepositDrawerSubScreen) => {
          if (currentSubScreen === DepositDrawerSubScreen.NATIVE_DEPOSIT) {
            trackCloseDepositCustom({
              reason: DepositNativeCloseReason.DEPOSIT_FAILED,
              orderRef: fundingTx?.orderRef,
            });
          }
        },
      });

      if (pendingDepositOrderRef === fundingTx?.orderRef) {
        dispatch(preferenceSlice.actions.setPendingDepositOrderRef(null));
      }
    },
    [t, openContact, showNotification, pendingDepositOrderRef, dispatch, trackCloseDepositCustom],
  );

  useWSDepositFailed({ cb: handleDepositFailed });
}
